import type { LinkEvidence, LinkSide, LinkStall } from '../types';

export const disconnectResponsibilityLabel = (value: string): string => ({
  node: '节点服务器问题',
  server: '游戏服务器问题',
  client: '用户本地网络问题',
  path: '用户到节点线路问题',
  unknown: '暂无法判断',
}[value] || '暂无法判断');

export const disconnectCauseLabel = (value: string): string => ({
  node_maintenance: '节点服务器维护或重启',
  agent_forwarding_error: '节点服务器转发异常',
  node_multi_target_disconnect: '节点服务器多线路同时中断',
  server_multi_node_disconnect: '游戏服务器集中断开',
  server_connection_closed: '游戏服务器方向连接中断',
  server_probe_confirm_timeout: '游戏服务器未完成确认',
  server_business_request_timeout: '游戏服务器未处理业务请求',
  game_client_unresponsive: '用户本地网络无响应',
  client_heartbeat_lost: '用户本地网络心跳中断',
  client_connection_closed: '用户本地网络连接已断开',
  client_multi_node_disconnect: '用户本地网络异常',
  insufficient_evidence: '证据不足',
  player_lan_or_pc: '玩家电脑或局域网问题',
  player_line_down: '玩家宽带线路中断',
  player_network: '玩家侧网络问题',
  player_reset: '玩家端重置连接',
  player_closed: '玩家主动关闭（不算掉线）',
  player_path_no_ack: '玩家到节点之间不通（未定位）',
  player_path_ping_ok: '玩家 IP 可 ping 通，但连接不通（未定位）',
  player_path_reset: '收到玩家方向的连接重置（来源未确认）',
  path_single_node: '只有该节点的线路中断，玩家宽带未整体中断',
  node_multi_player_disconnect: '节点上多个玩家同时中断',
  client_closed_after_quiet: '网络未中断，玩家端关闭（不算掉线）',
  both_sides_silent: '玩家和游服同时停止发送（无法判断先后）',
  duplicate_login_kicked: '同账号在别处登录，被顶下线（不算掉线）',
  server_forced_exit: '游戏服务器通知强制退出',
  server_connection_interrupted: '游戏服务器通知连接中断',
  server_relogin_requested: '游戏服务器要求重新登录',
  game_client_hung: '游戏客户端卡死',
  node_uplink_down: '节点服务器上行网络中断',
  node_both_legs_stalled: '节点服务器本机异常',
  node_route_to_server: '节点服务器到游服线路问题',
  server_network_unresponsive: '游戏服务器无响应',
  target_no_ack: '游服方向无响应（无法细分）',
  server_app_unresponsive: '游戏服务器程序无响应',
  server_closed: '游戏服务器主动断开',
  server_reset: '游戏服务器重置连接',
  target_path_failed: '游服方向连接异常',
}[value] || '证据不足');

export const disconnectCauseExplanation = (value: string, correlatedCount = 1): string => ({
  node_maintenance: '节点服务器或 Agent 发生停止、重启或升级，连接因此结束。',
  agent_forwarding_error: '节点服务器上的 Agent 在转发数据时发生错误并关闭连接。',
  node_multi_target_disconnect: `同一节点服务器在相近时间有 ${correlatedCount} 条连接、多个游戏服务器目标同时结束，更符合节点服务器网络异常。`,
  server_multi_node_disconnect: `同一游戏服务器目标在多台节点服务器上同期出现 ${correlatedCount} 条连接结束，排除了单台节点服务器的孤立故障，更符合游戏服务器维护或故障。`,
  server_connection_closed: '游戏服务器方向出现关闭、重置或读写失败；现有TCP证据可定位到目标方向，但不能单独区分游戏服务器本身与到游戏服务器的线路。',
  server_probe_confirm_timeout: '用户本地网络已经响应原生探测，但游戏服务器没有在预期时间内完成确认。',
  server_business_request_timeout: '请求已发送到游戏服务器，但游戏服务器没有在预期时间内返回业务响应。',
  game_client_unresponsive: '游戏服务器的探测已经到达，但用户本地网络没有响应。',
  client_heartbeat_lost: '节点服务器持续收不到用户本地连接的真实心跳，说明用户本地网络链路已经异常。',
  client_connection_closed: '用户本地网络先关闭或重置了TCP连接。',
  client_multi_node_disconnect: `同一用户本地网络在多台节点服务器、多个游戏目标上同期出现 ${correlatedCount} 条读写错误，共同故障域是用户本地网络。`,
  insufficient_evidence: '当前证据不足以可靠判断责任方，请结合TCP观察和原始错误继续排查。',
  player_lan_or_pc: '玩家侧不再确认数据，但节点同时能 ping 通玩家 IP：玩家路由器和宽带在线，问题在其后的电脑、WiFi、局域网或代理软件。',
  player_line_down: '玩家侧不再确认数据，节点 ping 玩家 IP 无回应，而该 IP 平时可 ping 通：玩家宽带线路中断。',
  player_network: '玩家侧不再确认数据，玩家 IP 平时就不响应 ping，只能确定是玩家侧网络问题，无法细分到线路或电脑。',
  player_reset: '此前两段链路都正常，玩家端发来了重置：通常是代理软件、杀毒软件或路由器主动断开。',
  player_closed: '此前两段链路都正常，玩家端正常关闭了连接：玩家主动退出游戏，不算掉线。',
  player_path_no_ack: '节点发给玩家的数据没有得到确认，玩家 IP 平时也不响应 ping。只看这条连接，无法确认断在玩家一侧还是中间线路。',
  player_path_ping_ok: '节点发给玩家的数据没有得到确认，但能 ping 通玩家 IP。可能是玩家电脑或局域网，也可能是这条连接在线路上被丢弃。',
  player_path_reset: '节点收到了玩家方向发来的连接重置。重置可能来自玩家电脑、路由器或中间网络设备，只看这条连接无法确认来源。',
  path_single_node: '同一玩家 IP 在其他节点的连接同期仍正常，说明玩家宽带没有整体中断，问题在玩家到该节点这一段。无法进一步确定是哪家运营商的线路。',
  node_multi_player_disconnect: `同一时刻该节点上多个不同 IP 的 ${correlatedCount} 条连接一起中断，共同点是这个节点或其上游线路。`,
  client_closed_after_quiet: '玩家方向曾静默，随后恢复发送数据，再由玩家端正常关闭。网络没有中断，不算掉线。',
  both_sides_silent: '玩家和游服几乎同时不再发送数据，两边仍在确认数据，说明网络未断，但无法判断哪一方先停止。',
  duplicate_login_kicked: '游戏服务器向本客户端下发了下线通知（同一账号在别处登录）。服务器随后不再发送数据，也不会主动关闭连接，直到玩家确认弹窗才断开。这是账号被顶号，不是节点或游戏服务器故障，不计入掉线。',
  server_forced_exit: '游戏服务器向本客户端下发了“强制退出”通知，随后不再发送数据，直到玩家确认弹窗才断开。是游戏服务器主动结束了这个账号的会话（例如被管理员踢出或异常行为检测），不是节点故障。',
  server_connection_interrupted: '游戏服务器向本客户端下发了“连接中断，请稍后再试”通知，随后不再发送数据，直到玩家确认弹窗才断开。属于游戏服务器一侧结束会话。',
  server_relogin_requested: '游戏服务器向本客户端下发了“请重新登录游戏，敬请谅解”通知，随后不再发送数据，直到玩家确认弹窗才断开。是游戏服务器要求这个账号重新登录。',
  game_client_hung: '玩家电脑仍在确认数据（网络正常），但游戏客户端不再发送任何内容：游戏客户端卡死。',
  node_uplink_down: '断开前后节点服务器 ping 公共网络持续不通：节点服务器上行网络中断。',
  node_both_legs_stalled: '玩家侧和游服侧在同一秒内同时停止确认数据，而节点服务器公共网络正常：节点服务器本机异常（网卡或负载），中等置信度。',
  node_route_to_server: '游服侧先停止确认数据，同期节点服务器 ping 游服不通但公共网络正常：节点服务器到游服的线路问题。',
  server_network_unresponsive: '游服侧先停止确认数据，但节点服务器同期仍能 ping 通游服：游戏服务器无响应。',
  target_no_ack: '游服侧先停止确认数据；游服不响应 ping，无法区分是节点服务器到游服线路还是游服本身。',
  server_app_unresponsive: '游服仍在确认数据（网络正常），但不再发送任何内容：游戏服务器程序无响应。',
  server_closed: '此前两段链路都正常，游戏服务器主动关闭了连接：通常是踢下线或维护。',
  server_reset: '此前两段链路都正常，游戏服务器发来了重置。',
  target_path_failed: '节点服务器到游服的连接异常结束，但没有检测到卡顿过程，无法细分是线路还是游服。',
}[value] || '当前没有对应的归因说明，请结合TCP观察和原始错误继续排查。');

/**
 * What a player is told about one disconnect. Plain language only: no ping, TCP,
 * heartbeats or other networking terms. Each text says whose side it was and what
 * to do next. `counts` is false for exits that are not a real disconnect.
 */
export interface PlayerDisconnectText {
  label: string;
  detail: string;
  counts: boolean;
}

const NO_FAULT = (label: string, detail: string): PlayerDisconnectText => ({ label, detail, counts: false });
const DROP = (label: string, detail: string): PlayerDisconnectText => ({ label, detail, counts: true });

const PLAYER_TEXT: Record<string, PlayerDisconnectText> = {
  // Not a disconnect
  player_closed: NO_FAULT('玩家主动退出游戏', '玩家自己退出了游戏，不算掉线。'),
  duplicate_login_kicked: NO_FAULT('账号在其他位置登录，被挤下线',
    '同一个账号在另一台设备或另一个窗口登录，原来的连接被游戏挤下线，不算掉线。'),
  client_closed_after_quiet: NO_FAULT('网络正常，游戏端关闭了连接',
    '游戏连接当时网络是通的，随后由游戏端自己关闭，不算网络掉线。'),

  // Between the player and the node: not proven to be either side
  player_path_no_ack: DROP('玩家到节点之间的网络中断',
    '玩家和节点服务器之间的网络当时不通，暂时无法确定断在玩家一侧还是中间线路。'),
  player_path_ping_ok: DROP('玩家到节点之间的连接中断',
    '玩家的宽带在线，但这条游戏连接当时不通。可能是玩家电脑或家庭网络，也可能是中间线路，暂时无法确定。'),
  player_path_reset: DROP('玩家到节点之间的连接被断开',
    '这条游戏连接在玩家到节点服务器之间被断开，暂时无法确定是玩家设备还是中间线路断开的。'),
  path_single_node: DROP('该节点的线路中断，玩家宽带正常',
    '玩家在其他节点的连接当时都正常，只有这个节点的线路中断，不是玩家宽带的问题。'),
  both_sides_silent: DROP('原因暂无法确定',
    '当时网络是通的，但玩家和游戏服务器几乎同时停止了通信，暂时无法确定是哪一方的原因。'),

  // The player's side
  player_network: DROP('玩家网络中断',
    '当时玩家这边的网络出现问题，游戏因此断开。常见原因是 WiFi、网线、路由器或宽带不稳定。'),
  player_lan_or_pc: DROP('玩家电脑或家庭网络设备故障',
    '玩家的宽带是通的，但电脑、WiFi 或路由器当时出了问题，游戏因此断开。'),
  player_line_down: DROP('玩家宽带中断',
    '当时玩家家里的宽带网络中断，游戏因此断开。'),
  player_reset: DROP('玩家电脑上的软件断开了连接',
    '玩家电脑或路由器上的软件（例如杀毒软件、加速器、路由器的防护功能）主动断开了游戏连接。'),
  game_client_hung: DROP('游戏客户端卡死',
    '游戏客户端当时没有响应（卡住了），连接因此断开。'),
  client_connection_closed: DROP('玩家一侧先断开了连接',
    '这次是玩家一侧先断开的连接，可能是网络波动，也可能是玩家主动退出。'),
  client_heartbeat_lost: DROP('玩家网络长时间无信号',
    '当时长时间收不到玩家游戏发来的信息，通常是玩家的网络或电脑出了问题。'),
  game_client_unresponsive: DROP('玩家网络无响应',
    '游戏服务器联系玩家的客户端时没有得到回应，通常是玩家一侧的网络出了问题。'),
  client_multi_node_disconnect: DROP('玩家网络中断（多条连接同时断开）',
    '玩家通过多条线路的游戏连接在同一时间一起断开，说明问题出在玩家自己的网络。'),

  // The proxy server
  node_multi_player_disconnect: DROP('节点服务器网络故障（多位玩家同时断开）',
    '同一台节点服务器上多位玩家在同一时间一起断开，说明是节点服务器或它的上游网络出了问题。'),
  node_maintenance: DROP('节点服务器维护',
    '节点服务器正在维护或升级，连接因此断开。'),
  agent_forwarding_error: DROP('节点服务器处理出错',
    '节点服务器当时出现处理错误，连接因此断开。原因在节点服务器一侧。'),
  node_uplink_down: DROP('节点服务器网络中断',
    '节点服务器自己的网络当时中断，游戏因此断开。原因在节点服务器一侧。'),
  node_both_legs_stalled: DROP('节点服务器运行不稳定',
    '节点服务器当时运行不稳定，游戏因此断开。原因在节点服务器一侧。'),
  node_route_to_server: DROP('节点服务器到游戏服务器的线路故障',
    '从节点服务器到游戏服务器之间的网络当时出现问题，游戏因此断开。原因在这段线路上，不在玩家的网络。'),
  node_multi_target_disconnect: DROP('节点服务器网络故障（多条连接同时断开）',
    '同一台节点服务器上的多个连接在同一时间一起断开，说明是节点服务器的网络出了问题。'),

  // The game server
  server_network_unresponsive: DROP('游戏服务器无响应',
    '当时游戏服务器没有响应，游戏因此断开。原因在游戏服务器一侧，不是玩家的网络。'),
  server_app_unresponsive: DROP('游戏服务器卡死',
    '当时游戏服务器没有响应（卡住了或正在维护），游戏因此断开。原因在游戏服务器一侧。'),
  target_no_ack: DROP('通往游戏服务器的网络无响应',
    '当时通往游戏服务器的网络没有响应，暂时分不清是线路的问题还是游戏服务器本身的问题。'),
  server_closed: DROP('游戏服务器主动结束连接',
    '游戏服务器主动结束了这次连接，可能是服务器维护，也可能是把账号踢下线。'),
  server_reset: DROP('游戏服务器中断连接',
    '游戏服务器中断了这次连接，原因在游戏服务器一侧。'),
  target_path_failed: DROP('与游戏服务器的连接异常结束',
    '与游戏服务器的连接异常结束，暂时无法判断具体原因。'),
  server_business_request_timeout: DROP('游戏服务器未响应操作请求',
    '玩家的操作发出后，游戏服务器长时间没有回应，游戏因此断开。原因在游戏服务器一侧。'),
  server_probe_confirm_timeout: DROP('游戏服务器未按时响应',
    '游戏服务器当时没有按时回应，连接因此断开。原因在游戏服务器一侧。'),
  server_connection_closed: DROP('游戏服务器方向连接中断',
    '与游戏服务器之间的连接断开了，暂时分不清是线路的问题还是游戏服务器本身的问题。'),
  server_multi_node_disconnect: DROP('游戏服务器集中断开（维护或故障）',
    '同一时间有很多玩家都从这个游戏服务器掉线，说明是游戏服务器本身维护或出了故障，不是玩家的问题。'),
  server_forced_exit: DROP('游戏服务器强制账号退出',
    '游戏服务器要求这个账号退出游戏（例如被管理员处理）。'),
  server_connection_interrupted: DROP('游戏服务器通知连接中断',
    '游戏服务器通知游戏连接中断，原因在游戏服务器一侧。'),
  server_relogin_requested: DROP('游戏服务器要求重新登录',
    '游戏服务器要求这个账号重新登录游戏。'),

  insufficient_evidence: DROP('原因暂无法确定',
    '这次断开暂时没能确定原因，需要结合掉线的大致时间和角色名继续核对。'),
};

export const disconnectPlayerText = (code: string): PlayerDisconnectText =>
  PLAYER_TEXT[code] || PLAYER_TEXT.insufficient_evidence;

/** Every cause code that has player wording, for tests. */
export const playerTextCodes = (): string[] => Object.keys(PLAYER_TEXT);

const endKindLabel = (value: string): string => ({
  fin: '正常关闭',
  rst: '被重置',
  timeout: '超时',
  unreachable: '不可达',
  broken_pipe: '对端已关闭',
  other_error: '其他错误',
  agent_close: '节点服务器关闭',
  still_open: '仍连接（另一侧先结束）',
}[value] || '未知');

const clock = (ms: number) => new Date(ms).toLocaleTimeString('zh-CN', { hour12: false });

const stallText = (stall: LinkStall | undefined): string => {
  if (!stall) return '无卡顿';
  const what = stall.kind === 'no_ack' ? '不再确认数据（网络层）' : '仍确认但不发数据（程序层）';
  const end = stall.ended_at_ms ? `，${clock(stall.ended_at_ms)} 恢复` : '';
  return `${clock(stall.started_at_ms)} 起${what}${end}`;
};

const sideLine = (label: string, side: LinkSide): string =>
  `${label}：${stallText(side.stall || side.recent_stall)}；结束方式：${endKindLabel(side.end_kind)}`;

/** One line per measurement in the agent's link evidence; empty for older agents. */
export const linkEvidenceLines = (evidence?: { link?: LinkEvidence }): string[] => {
  const link = evidence?.link;
  if (!link) return [];
  const lines = [sideLine('玩家侧', link.client), sideLine('游服侧', link.target)];
  const ping = link.peer_ping;
  if (ping) {
    if (ping.error) {
      lines.push('卡顿时 ping 玩家：发送失败');
    } else {
      const rtt = ping.rtt_ms !== undefined ? `，${ping.rtt_ms}ms` : '';
      const usual = ping.baseline_replied === true ? '；该 IP 平时可 ping 通'
        : ping.baseline_replied === false ? '；该 IP 平时不响应 ping' : '';
      lines.push(`卡顿时 ping 玩家：${ping.received}/${ping.sent} 回复${rtt}${usual}`);
    }
  }
  const base = link.node_baseline;
  if (base) {
    const target = base.target_ever_replied
      ? `，游服不通 ${base.target_ticks_down}/${base.target_ticks} 秒`
      : '，游服不响应 ping';
    lines.push(`节点自测（${base.ticks} 秒）：公共网络不通 ${base.anchor_ticks_down} 秒${target}`);
  }
  return lines;
};

export const filterAuditColumnsForCharacterPlugin = <T extends { key?: string }>(
  columns: T[],
  enabled: boolean,
): T[] => enabled ? columns : columns.filter((item) => item.key !== 'role');
